/**
 * Formula transparency block (task 14.1, R5.8, R7.5, UI spec §5.5).
 *
 * States the calculation chain behind Demand_Hours, Capacity_Hours and
 * Utilisation, plus the assumption-set version, effective date and allocation
 * method a reader needs to trust a figure (R5.8's disclosure). Shared by all four
 * Manpower screens, so the wording of a formula lives in one place.
 *
 * Every string resolves from the catalogue (R18.9); the formulas are prose about
 * the domain, not code, so they are translatable like any other copy.
 */
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';

export interface FormulaTransparencyBlockProps {
  /** The Effort_Assumption set version the figures on screen were calculated against. */
  readonly assumptionSetVersion?: string;
  readonly effectiveDate?: string;
  /** How demand is spread across periods, when the screen shows period figures. */
  readonly allocationMethod?: string;
}

export function FormulaTransparencyBlock({
  assumptionSetVersion,
  effectiveDate,
  allocationMethod,
}: FormulaTransparencyBlockProps = {}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    // UI spec §5.5's own treatment: the subtle fill, a 1px `--line` border and the
    // 4px radius of a control rather than a card, because this block is a
    // disclosure appended to a screen and not a content card of its own. Body copy
    // is 11.5px muted at a 1.6 line height, with the formula labels in `--txt`.
    <Card className="border-line bg-bg-subtle gap-3 rounded-[var(--radius-control)] p-4">
      <p className="text-[13px] font-semibold text-txt">{t('manpower.formula.heading')}</p>
      <div className="text-muted-foreground space-y-2 text-[11.5px] leading-relaxed">
        <Formula label={t('manpower.formula.demandLabel')} body={t('manpower.formula.demand')} />
        <Formula label={t('manpower.formula.capacityLabel')} body={t('manpower.formula.capacity')} />
        <Formula
          label={t('manpower.formula.utilisationLabel')}
          body={t('manpower.formula.utilisation')}
        />
        {allocationMethod !== undefined && (
          <Formula label={t('manpower.formula.allocationLabel')} body={allocationMethod} />
        )}
        {assumptionSetVersion !== undefined && (
          <Formula
            label={t('manpower.formula.assumptionSetLabel')}
            body={
              effectiveDate === undefined
                ? assumptionSetVersion
                : t('manpower.formula.assumptionSetWithDate', {
                    version: assumptionSetVersion,
                    date: effectiveDate,
                  })
            }
          />
        )}
      </div>
    </Card>
  );
}

function Formula({ label, body }: { label: string; body: string }): React.JSX.Element {
  return (
    <div>
      <strong className="text-txt">{label}</strong> {body}
    </div>
  );
}
